import { debounce } from "lodash";
import React, { useEffect } from "react";
import isBetweenInclusive from "../../util/isBetweenInclusive";
import RawUnderline from "./RawUnderline";
import FeedbackType from "../../FeedbackType";
import useHoverDelay from "./hooks/useHoverDelay";
import usePrevious from "./hooks/usePrevious";

export type UnderlineProps = {
  height: number;
  width: number;
  top: number;
  left: number;
  type: FeedbackType;
  gated: boolean;
  /**
   * This element is meant to be underneath the Underline when painted in the browser.
   *
   * More specifically, Underline uses this element to check for mouse events, which will only activate hovering
   * using the element's mouse events.
   */
  observedElement: HTMLElement;

  // The dimensions of the observed element... to lay out the mouse listening "box"

  observedTop: number;
  observedLeft: number;

  /**
   * Whether the element is highlighted or not
   */
  isHighlighted: boolean;

  onHoverStarted?: () => void;
  onHoverEnded?: () => void;
};

const capture = { capture: true };

/**
 * Lays out an underline and highlight at the coordinates and dimensions specified. This component is responsible for controlling an
 * underlying raw Underline, which is an underlying styled element.
 *
 * Enables users to interact with the observedElement below this underline, which is most typically a text editing element,
 * e.g. a TextArea, or content editable div.
 *
 */
const Underline: React.FC<UnderlineProps> = ({
  gated,
  height,
  width,
  top,
  left,
  type,
  observedElement,
  observedLeft,
  observedTop,
  isHighlighted,
  onHoverStarted,
  onHoverEnded,
}: UnderlineProps) => {
  const { isHovering, markIsHovering, markIsNotHovering } = useHoverDelay();
  const wasHovering = usePrevious(isHovering) || false; // avoid undefined, always a boolean

  // Listens to observedElement for mouse movements, sets hovering if the mouse hits the Underline within the observedElement
  // Listens when the mouse leaves the observed element, sets hovering to false
  useEffect(() => {
    const mouseMoveListener = debounce((mouseEvent: MouseEvent) => {
      const mouseLeft = mouseEvent.clientX - observedLeft;
      const mouseTop = mouseEvent.clientY - observedTop;

      // if the mouse is within the element of the underline/highlight
      const isMouseWithinBounds =
        isBetweenInclusive(mouseLeft, left, left + width) &&
        isBetweenInclusive(mouseTop, top, top + height);

      isMouseWithinBounds ? markIsHovering() : markIsNotHovering();
    }, 25);
    observedElement?.addEventListener("mousemove", mouseMoveListener, capture);
    observedElement?.addEventListener("mouseout", mouseMoveListener, capture);

    return () => {
      mouseMoveListener.cancel();
      observedElement?.removeEventListener(
        "mousemove",
        mouseMoveListener,
        capture
      );
      observedElement?.removeEventListener(
        "mouseout",
        mouseMoveListener,
        capture
      );
    };
  }, [
    observedElement,
    height,
    width,
    left,
    top,
    markIsHovering,
    markIsNotHovering,
    observedLeft,
    observedTop,
  ]);

  useEffect(() => {
    if (isHovering !== wasHovering) {
      isHovering ? onHoverStarted?.() : onHoverEnded?.();
    }
  }, [isHovering, wasHovering, onHoverEnded, onHoverStarted]);
  return (
    <RawUnderline
      height={height}
      width={width}
      left={left}
      top={top}
      type={type}
      isHighlighted={isHighlighted}
      gated={gated}
    />
  );
};

export default Underline;
